// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { Bell, X, ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import {
  useNotifications,
  useDismissNotification,
} from '@/app/features/notifications';

/**
 * Notification bell icon with dropdown panel.
 * Shows a badge when there are active notifications.
 * Placed in the sidebar footer alongside user info.
 */
export function NotificationBell() {
  const { data } = useNotifications();
  const dismiss = useDismissNotification();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  interface NotificationItemShape {
    id: string;
    type: string;
    title: string;
    description: string;
    action_url: string;
    dismissable: boolean;
  }
  const items: NotificationItemShape[] = data?.items ?? [];
  const count: number = data?.unread_count ?? 0;

  // Close on click outside.
  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label={`Notifications${count > 0 ? ` (${count} pending)` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell className="h-4.5 w-4.5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full left-0 z-[60] mb-2 w-80 overflow-hidden rounded-lg border border-border bg-background shadow-lg"
            role="menu"
          >
            {/* Header */}
            <div className="border-b border-border/50 px-4 py-3">
              <p className="text-[14px] font-medium text-foreground">
                Notifications
              </p>
            </div>

            {/* Items */}
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-[13px] text-muted-foreground">
                    All caught up
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border/50">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="group relative px-4 py-3 transition-colors hover:bg-accent/50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium text-foreground">
                            {item.title}
                          </p>
                          <p className="mt-0.5 text-[12px] text-muted-foreground">
                            {item.description}
                          </p>
                          <Link
                            to={item.action_url}
                            onClick={() => setOpen(false)}
                            className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-primary transition-colors hover:text-primary/80"
                          >
                            Go
                            <ArrowRight className="h-3 w-3" />
                          </Link>
                        </div>

                        {item.dismissable && (
                          <button
                            onClick={() => dismiss.mutate(item.id)}
                            className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-all hover:bg-accent hover:text-foreground group-hover:opacity-100"
                            aria-label={`Dismiss: ${item.title}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
